import ReplyOutlinedIcon from "@mui/icons-material/ReplyOutlined"
import LoadingButton from "@mui/lab/LoadingButton"
import Alert from "@mui/material/Alert"
import Button from "@mui/material/Button"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import { formatDistanceToNow } from "date-fns"
import { useCallback, useEffect, useRef, useState } from "react"

import { useAuthorizedAxiosClient } from "../../../common/axios"
import Column from "../../../common/components/Column"
import { FEEDBACK_CHOICE_LABELS } from "../candidate-details/activity-timeline-column/activity-tabs/feedbackEventDisplay"
import DialogHeader from "./DialogHeader"

function formatReplyStatus(reply) {
  if (reply.send_status === "sent") {
    return "Sent to client"
  }
  if (reply.send_status === "failed") {
    return "Send failed"
  }
  return "Sending…"
}

export function FeedbackClientReplyHistory({ replies }) {
  if (!replies?.length) {
    return null
  }
  return (
    <Column gap={1} sx={{ mt: 1.25 }}>
      <Typography variant="caption" color="text.secondary" fontWeight={600}>
        Responses sent to client
      </Typography>
      {replies.map((reply) => (
        <Column
          key={reply.id}
          gap={0.35}
          sx={{
            p: 1,
            borderRadius: "10px",
            border: "1px solid",
            borderColor: "colors.gray200",
            backgroundColor: "colors.gray50",
          }}
        >
          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
            {reply.body}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {[
              reply.performed_by_name ? `By ${reply.performed_by_name}` : null,
              reply.created
                ? `${formatDistanceToNow(new Date(reply.created))} ago`
                : null,
              formatReplyStatus(reply),
              reply.recipient_email ? `To ${reply.recipient_email}` : null,
            ]
              .filter(Boolean)
              .join(" · ")}
          </Typography>
          {reply.send_status === "failed" && reply.send_error ? (
            <Typography variant="caption" color="error">
              {reply.send_error}
            </Typography>
          ) : null}
        </Column>
      ))}
    </Column>
  )
}

export default function FeedbackClientReplySection({
  feedbackEventId,
  feedbackType,
  feedbackComments,
  candidateName,
  clientReplies = [],
  onRepliesUpdated,
  compact = false,
  showHistory = true,
}) {
  const axios = useAuthorizedAxiosClient()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [replyContext, setReplyContext] = useState(null)
  const [loadingContext, setLoadingContext] = useState(false)
  const [draft, setDraft] = useState("")
  const [sendState, setSendState] = useState("idle")
  const [errorMessage, setErrorMessage] = useState("")
  const onRepliesUpdatedRef = useRef(onRepliesUpdated)

  useEffect(() => {
    onRepliesUpdatedRef.current = onRepliesUpdated
  }, [onRepliesUpdated])

  const feedbackLabel =
    FEEDBACK_CHOICE_LABELS[feedbackType] || feedbackType || "Feedback"

  const loadContext = useCallback(() => {
    if (!feedbackEventId) {
      return
    }
    setLoadingContext(true)
    axios
      .get(
        `/crm/api/activity/feedback-events/${feedbackEventId}/reply-context/`
      )
      .then(({ data }) => {
        setReplyContext(data)
        onRepliesUpdatedRef.current?.(data.client_replies ?? [])
      })
      .catch(() => {
        setReplyContext(null)
        setErrorMessage("Could not load reply details.")
      })
      .finally(() => setLoadingContext(false))
  }, [axios, feedbackEventId])

  useEffect(() => {
    if (dialogOpen) {
      loadContext()
    }
  }, [dialogOpen, loadContext])

  function openDialog(event) {
    event?.stopPropagation?.()
    setDraft("")
    setErrorMessage("")
    setSendState("idle")
    setDialogOpen(true)
  }

  function closeDialog() {
    if (sendState === "sending") {
      return
    }
    setDialogOpen(false)
  }

  function handleSend() {
    const body = draft.trim()
    if (!body || sendState === "sending") {
      return
    }
    setSendState("sending")
    setErrorMessage("")
    axios
      .post(`/crm/api/activity/feedback-events/${feedbackEventId}/replies/`, {
        body,
      })
      .then(({ data }) => {
        const nextReplies = [
          ...(replyContext?.client_replies ?? clientReplies),
          data,
        ]
        setReplyContext((current) =>
          current ? { ...current, client_replies: nextReplies } : current
        )
        onRepliesUpdated?.(nextReplies)
        setSendState("success")
        setDialogOpen(false)
        setDraft("")
      })
      .catch((error) => {
        const detail = error?.response?.data
        if (detail?.id && detail?.send_status) {
          const nextReplies = [
            ...(replyContext?.client_replies ?? clientReplies),
            detail,
          ]
          setReplyContext((current) =>
            current ? { ...current, client_replies: nextReplies } : current
          )
          onRepliesUpdated?.(nextReplies)
        }
        const message =
          detail?.body?.[0] ||
          detail?.detail ||
          detail?.recipient?.[0] ||
          "Could not send this response. Try again."
        setErrorMessage(
          typeof message === "string"
            ? message
            : "Could not send this response."
        )
        setSendState("failed")
      })
  }

  const replies = replyContext?.client_replies ?? clientReplies
  const recipient = replyContext?.recipient
  const canSend = replyContext?.can_send ?? false
  const blockReason =
    replyContext?.recipient_error || replyContext?.can_send_reason || null

  return (
    <>
      <Column
        gap={0.5}
        sx={{ mt: compact ? 0.75 : 1, alignItems: "flex-start" }}
      >
        <Button
          size="small"
          startIcon={<ReplyOutlinedIcon fontSize="small" />}
          onClick={openDialog}
          disabled={!feedbackEventId}
          sx={{ textTransform: "none", fontWeight: 600 }}
        >
          Reply to client
        </Button>
        {showHistory ? <FeedbackClientReplyHistory replies={replies} /> : null}
      </Column>

      <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogHeader title="Reply to client" onClose={closeDialog} />
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
        >
          {loadingContext ? (
            <Typography variant="body2" color="text.secondary">
              Loading…
            </Typography>
          ) : null}
          {candidateName ? (
            <Typography variant="body2" color="text.secondary">
              Candidate: <strong>{candidateName}</strong>
            </Typography>
          ) : null}
          <Column gap={0.5}>
            <Typography variant="body2" fontWeight={600}>
              Original feedback
            </Typography>
            <Typography variant="body2">{feedbackLabel}</Typography>
            {feedbackComments ? (
              <Typography
                variant="body2"
                sx={{ whiteSpace: "pre-wrap", color: "text.secondary" }}
              >
                {feedbackComments}
              </Typography>
            ) : null}
          </Column>
          {recipient ? (
            <Typography variant="body2">
              Client recipient:{" "}
              <strong>
                {recipient.name || recipient.email}
                {recipient.name && recipient.email
                  ? ` (${recipient.email})`
                  : ""}
              </strong>
            </Typography>
          ) : null}
          {blockReason ? <Alert severity="warning">{blockReason}</Alert> : null}
          {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}
          <TextField
            label="Your response"
            multiline
            minRows={4}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            disabled={sendState === "sending"}
            fullWidth
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeDialog} disabled={sendState === "sending"}>
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            loading={sendState === "sending"}
            disabled={!draft.trim() || !canSend || loadingContext}
            onClick={handleSend}
          >
            Send response
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  )
}
